import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isInAppBrowser } from '../../utils/browserDetector';

// CSS Variables matching app-mothai.js theme
const CSS_VARS = {
  gold: '#D4AF37',
  goldLight: '#F4E4BA',
  goldDark: '#996515',
  greenDeep: '#1A3C34',
  greenForest: '#2D5A4A',
  greenHerb: '#4A7C59',
  greenLeaf: '#6B9B7A',
  cream: '#FDF8F0',
  creamDark: '#E8DFD0',
  white: '#FFFFFF',
  black: '#0D1B16',
};

const PWAInstallPrompt = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [hasCheckedPrompt, setHasCheckedPrompt] = useState(false);

  useEffect(() => {
    // Reset when route changes
    setHasCheckedPrompt(false);
    setShowInstallPrompt(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      
      // Log for debugging
      console.log('PWA install prompt available');
      
      // ถ้าเปิดจาก In-App Browser (LINE/Facebook) ให้แสดง prompt ทันที
      // ถ้าไม่ใช่ ให้รอสักครู่ก่อนแสดง (เพื่อไม่ให้รบกวน user)
      if (isInAppBrowser()) {
        setShowInstallPrompt(true);
      } else {
        // Delay สำหรับหน้า "/" เพื่อให้ user เห็นหน้า welcome ก่อน
        const delay = location.pathname === '/' ? 3000 : 2000;
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, delay);
      }
      
      setHasCheckedPrompt(true);
    };

    // Only add listener if not already installed
    if (!window.matchMedia('(display-mode: standalone)').matches) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      
      // Fallback: ถ้า after 5 seconds ยังไม่มี event และไม่ใช่ In-App Browser
      // ให้แสดง prompt เอง (สำหรับกรณีที่ browser ไม่ support beforeinstallprompt)
      if (!isInAppBrowser()) {
        const fallbackTimer = setTimeout(() => {
          if (!hasCheckedPrompt && !deferredPrompt) {
            // ตรวจสอบว่าเป็น mobile device และยังไม่ได้ติดตั้ง
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (isMobile && !window.matchMedia('(display-mode: standalone)').matches) {
              // แสดง prompt แบบ manual (แนะนำให้ user ติดตั้งเอง)
              const delay = location.pathname === '/' ? 3000 : 2000;
              setTimeout(() => {
                setShowInstallPrompt(true);
              }, delay);
            }
          }
        }, 5000);
        
        return () => {
          clearTimeout(fallbackTimer);
        };
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [location.pathname, hasCheckedPrompt, deferredPrompt]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }

      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    } else {
      // Fallback: ถ้าไม่มี deferredPrompt ให้แสดงคำแนะนำ
      // สำหรับ iOS Safari
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        alert('สำหรับ iOS: กดปุ่ม Share (แชร์) แล้วเลือก "Add to Home Screen" (เพิ่มไปที่หน้าจอหลัก)');
      } else if (/Android/i.test(navigator.userAgent)) {
        alert('สำหรับ Android: กดเมนู (⋮) แล้วเลือก "Add to Home screen" (เพิ่มไปที่หน้าจอหลัก)');
      } else {
        alert('กรุณาใช้เมนูของ Browser เพื่อติดตั้งแอป');
      }
      setShowInstallPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
  };

  if (!showInstallPrompt) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 text-white px-4 py-3 rounded-lg shadow-lg z-50 sm:left-auto sm:right-4 sm:w-80"
      style={{ 
        background: `linear-gradient(135deg, ${CSS_VARS.greenDeep} 0%, ${CSS_VARS.greenForest} 100%)`,
        fontFamily: "'Sarabun', sans-serif"
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
            >
              {/* Book icon with leaf - matching the app theme */}
              <path d="M6 4C5.44772 4 5 4.44772 5 5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V5C19 4.44772 18.5523 4 18 4H6Z" fill={CSS_VARS.gold} stroke={CSS_VARS.goldDark} strokeWidth="1"/>
              <path d="M7 6H17V8H7V6Z" fill={CSS_VARS.greenDeep} opacity="0.6"/>
              <path d="M7 10H17V12H7V10Z" fill={CSS_VARS.greenDeep} opacity="0.6"/>
              <path d="M7 14H13V16H7V14Z" fill={CSS_VARS.greenDeep} opacity="0.6"/>
              {/* Leaf decoration */}
              <path d="M16 12C16 12 17 11 17.5 11.5C18 12 18 13 17.5 13.5C17 14 16 14 15.5 13.5C15 13 15.5 12 16 12Z" fill={CSS_VARS.greenHerb}/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base leading-tight text-white" style={{ fontFamily: "'Kanit', sans-serif" }}>ติดตั้งแอป ติวหมอไทย</h3>
            <p className="text-sm opacity-95 mt-0.5 text-white">ใช้งานได้เร็วขึ้นและสะดวกกว่า</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-sm rounded-lg font-medium transition-opacity duration-200 hover:opacity-100"
            style={{ 
              backgroundColor: CSS_VARS.white, 
              color: CSS_VARS.greenDeep,
              opacity: 0.9,
              fontFamily: "'Kanit', sans-serif"
            }}
          >
            ปิด
          </button>
          <button
            onClick={handleInstallClick}
            className="px-4 py-2 text-sm rounded-lg font-medium transition-colors duration-200 shadow-sm hover:opacity-90"
            style={{ 
              background: `linear-gradient(135deg, ${CSS_VARS.gold} 0%, ${CSS_VARS.goldDark} 100%)`,
              color: CSS_VARS.black,
              fontFamily: "'Kanit', sans-serif"
            }}
          >
            ติดตั้ง
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;








